import type { ArtifactProto, ExecutionStepProto } from "build-ts/chat_session";
import { ArtifactType, artifactTypeFromJSON, ExecutionStatusType } from "build-ts/common";
import { DarwinToolType } from "build-ts/darwin_agent";
import type { IconType } from "react-icons";
import { AppIcons } from "constants/icons";

export type ToolExecutionRun = {
  steps: ExecutionStepProto[];
  toolType: DarwinToolType | undefined;
  /** True when the last step is TOOL_CALL_SUCCESS */
  completed: boolean;
  /** True when the last step is TOOL_CALL_FAILURE or ERROR_STATE */
  failed: boolean;
};

function isTerminalStep(status: ExecutionStatusType | undefined): boolean {
  if (status === undefined) return false;
  return (
    status === ExecutionStatusType.TOOL_CALL_SUCCESS ||
    status === ExecutionStatusType.TOOL_CALL_FAILURE ||
    status === ExecutionStatusType.ERROR_STATE
  );
}

/**
 * One UI row per tool invocation: starts at EXECUTING_TOOL_CALL, ends at
 * TOOL_CALL_SUCCESS / TOOL_CALL_FAILURE / ERROR_STATE. Intermediate statuses
 * update the same row's label.
 *
 * `EXECUTING_LLM_CALL` is omitted: it is not stored on live messages in `Chat.handleExecutionStep`
 * (the main loader already shows "Thinking…"), but persisted history can still contain it.
 */
export function groupExecutionStepsByToolRun(steps: ExecutionStepProto[] | undefined): ToolExecutionRun[] {
  if (!steps?.length) return [];

  const timelineSteps = steps.filter(s => s.status !== ExecutionStatusType.EXECUTING_LLM_CALL);
  if (!timelineSteps.length) return [];

  const groups: ToolExecutionRun[] = [];
  let current: ExecutionStepProto[] | null = null;
  let currentToolType: DarwinToolType | undefined;

  for (const step of timelineSteps) {
    const st = step.status;

    // File upload is a standalone immediately-completed run — no EXECUTING_TOOL_CALL wrapper.
    if (st === ExecutionStatusType.CREATING_FILE_ARTIFACT) {
      if (current) {
        groups.push({ steps: current, toolType: currentToolType, completed: false, failed: false });
        current = null;
        currentToolType = undefined;
      }
      groups.push({ steps: [step], toolType: undefined, completed: true, failed: false });
      continue;
    }

    if (st === ExecutionStatusType.EXECUTING_TOOL_CALL) {
      if (current) {
        groups.push({ steps: current, toolType: currentToolType, completed: false, failed: false });
      }
      current = [step];
      currentToolType = step.tool_type ?? undefined;
      continue;
    }

    if (!current) continue;

    current.push(step);
    if (isTerminalStep(st)) {
      const failed = st === ExecutionStatusType.TOOL_CALL_FAILURE || st === ExecutionStatusType.ERROR_STATE;
      const completed = st === ExecutionStatusType.TOOL_CALL_SUCCESS;
      groups.push({ steps: current, toolType: currentToolType, completed, failed });
      current = null;
    }
  }

  if (current) {
    groups.push({ steps: current, toolType: currentToolType, completed: false, failed: false });
  }
  return groups;
}

/** Icon for the live timeline, matched to the artifact kind each tool produces. */
export function toolRunStreamIcon(run: ToolExecutionRun): IconType {
  if (run.steps[0]?.status === ExecutionStatusType.CREATING_FILE_ARTIFACT) return AppIcons.dataframe;
  switch (run.toolType) {
    case DarwinToolType.ANALYZE_DATAFRAMES_TOOL:
      if (run.steps.some(s => s.status === ExecutionStatusType.BUILDING_CHART)) return AppIcons.chart;
      if (run.steps.some(s => s.status === ExecutionStatusType.BUILDING_REPORT)) return AppIcons.document;
      return AppIcons.dataframe;
    case DarwinToolType.DATAFRAME_TOOL:
    case DarwinToolType.KG_OBSERVATION_DATA:
      return AppIcons.database;
    case DarwinToolType.GET_PAST_JOB_RUNS:
    case DarwinToolType.BUILD_ANALYSIS_OUTPUT_TOOL:
    case DarwinToolType.SEARCH_WORKFLOW_RUNS_TOOL:
      return AppIcons.search;
    case DarwinToolType.SAVE_WORKFLOW_RUN_TOOL:
    case DarwinToolType.GET_WORKFLOW_RUN_DETAIL_TOOL:
      return AppIcons.workflowRun;
    case DarwinToolType.GET_SCENARIO_DETAIL_TOOL:
    case DarwinToolType.UPDATE_SCENARIO_METADATA_TOOL:
    case DarwinToolType.SAVE_SCENARIO_TOOL:
      return AppIcons.workflow;
    case DarwinToolType.LOAD_SKILL_TOOL:
      return AppIcons.loadSkill;
    case DarwinToolType.SKILL_TOOL:
    case DarwinToolType.AGENT_TOOL:
    default:
      return AppIcons.workflow;
  }
}

/** Artifacts in step order (streaming accumulation within the run). */
function collectArtifactsInOrder(steps: ExecutionStepProto[]): ArtifactProto[] {
  const out: ArtifactProto[] = [];
  for (const s of steps) {
    for (const a of s.artifacts ?? []) {
      out.push(a);
    }
  }
  return out;
}

/**
 * Returns the error message from the failure step of a failed tool run, or undefined
 * when the run did not fail or no error message was recorded.
 */
export function resolveErrorForFailedToolRun(run: ToolExecutionRun): string | undefined {
  if (!run.failed) return undefined;
  const failureStep = run.steps.find(
    s => s.status === ExecutionStatusType.TOOL_CALL_FAILURE || s.status === ExecutionStatusType.ERROR_STATE
  );
  return failureStep?.error ?? undefined;
}

/**
 * Artifacts that can be opened in the side panel for a **successful** tool run.
 * Skills do not expose a loadable artifact. Multiple `ANALYSIS_OUTPUT` (search) are all returned.
 */
export function resolveArtifactsForSuccessfulToolRun(run: ToolExecutionRun): ArtifactProto[] {
  if (!run.completed || run.failed) return [];

  if (run.toolType === DarwinToolType.SKILL_TOOL) return [];

  const ordered = collectArtifactsInOrder(run.steps);
  if (!ordered.length) return [];

  const analyses = ordered.filter(a => artifactTypeFromJSON(a.artifact_type) === ArtifactType.ANALYSIS_OUTPUT);
  if (analyses.length > 0) return analyses;

  const dataframes = ordered.filter(a => artifactTypeFromJSON(a.artifact_type) === ArtifactType.KG_DATAFRAME);
  if (dataframes.length > 0) return [dataframes[dataframes.length - 1]!];

  const dataframeAnalyses = ordered.filter(
    a => artifactTypeFromJSON(a.artifact_type) === ArtifactType.DATAFRAME_ANALYSIS
  );
  if (dataframeAnalyses.length > 0) return [dataframeAnalyses[dataframeAnalyses.length - 1]!];

  const observations = ordered.filter(a => artifactTypeFromJSON(a.artifact_type) === ArtifactType.KG_OBSERVATION);
  if (observations.length > 0) return [observations[observations.length - 1]!];

  const jobs = ordered.filter(a => artifactTypeFromJSON(a.artifact_type) === ArtifactType.SCENARIO_JOB);
  if (jobs.length > 0) return [jobs[jobs.length - 1]!];

  const scenarios = ordered.filter(a => artifactTypeFromJSON(a.artifact_type) === ArtifactType.SCENARIO);
  if (scenarios.length > 0) return [scenarios[scenarios.length - 1]!];

  const fileArtifacts = ordered.filter(a => artifactTypeFromJSON(a.artifact_type) === ArtifactType.FILE_ARTIFACT);
  if (fileArtifacts.length > 0) return fileArtifacts;

  const workflowRunSearches = ordered.filter(
    a => artifactTypeFromJSON(a.artifact_type) === ArtifactType.WORKFLOW_RUN_SEARCH
  );
  if (workflowRunSearches.length > 0) return [workflowRunSearches[workflowRunSearches.length - 1]!];

  const workflowRuns = ordered.filter(a => artifactTypeFromJSON(a.artifact_type) === ArtifactType.WORKFLOW_RUN);
  if (workflowRuns.length > 0) return [workflowRuns[workflowRuns.length - 1]!];

  return [];
}
